import { isAccommodationV2Item } from 'lib/checkout/checkoutUtils'
import type { LoyaltyMarginTolerance } from './getLoyaltyMarginTolerances'

/** svc-order's margin tolerance cap: the most keeping a quoted points price may cost LE per item, in AUD */
export const MAX_MARGIN_TOLERANCE_COST_AUD = 50

/**
 * Loyalty: after a price hold on Pay, svc-order accepts an accommodationV2 room's quoted points
 * margin when the points price didn't rise, or its margin tolerance cost is at most A$50. Returns
 * the quoted margin to keep for each session whose points price moved, or undefined when svc-order
 * wouldn't accept them all, so the customer reviews the change instead. `maxCost` is the A$50 cap
 * in the cart currency, undefined when it couldn't be priced. A points price that fell is kept on
 * any rate, as svc-order accepts a lower margin without a hold. A risen one is kept only on the
 * customer's original rate (an `available` enquiry): on a fallback rate the margin before a hold can
 * be below the quoted one, which svc-order rejects.
 */
function getLoyaltyMarginsWithinTolerance(
  cartItems: Array<App.Checkout.AnyItem>,
  sessions: App.CheckoutState['accommodationSessions'],
  tolerances: Array<LoyaltyMarginTolerance> | undefined,
  maxCost: number | undefined,
): Record<string, string> | undefined {
  if (!tolerances?.length) return undefined

  const sessionIdByItemId = new Map(
    cartItems.filter(isAccommodationV2Item).map(item => [item.itemId, item.sessionId]),
  )
  const tolerated = tolerances.flatMap(({ itemId, mx, cost }) => {
    const sessionId = sessionIdByItemId.get(itemId)
    const outcome = sessionId ? sessions[sessionId]?.outcome : undefined
    if (!sessionId || !outcome) return []
    if (cost > 0 && outcome !== 'available') return []
    return [[sessionId, mx] as const]
  })
  if (tolerated.length !== tolerances.length) return undefined

  const highestCost = Math.max(...tolerances.map(({ cost }) => cost))
  if (highestCost > 0 && (maxCost === undefined || highestCost > maxCost)) return undefined
  return Object.fromEntries(tolerated)
}

export default getLoyaltyMarginsWithinTolerance
